import React from "react";
import {
  Card,
  CardBody,
  Table,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input
} from "reactstrap";
import moment from "moment";
import Spinner from "../../components/base/Spinner";
import "./SnowFlakeDashboard.scss";

const SnowFlakeAttribution = ({
  attributionData,
  correlateData,
  corrStartDate,
  corrEndDate,
  currentPage,
  pageSize,
  totalRecords,
  totalPages,
  handlePageChange,
  setPageSize,
  setCurrentPage,
  loading,
  isPartner
}) => {
  return (
    <>
      {!isPartner && (
        <div>
          <Card className="table-card glass-card">
            <CardBody>
              <h5 className="card-title mb-3">
                Consumer Attribution (
                {moment(corrEndDate).diff(moment(corrStartDate), "days")} Days)
              </h5>
              <div
                className="custom-table-responsive position-relative"
                style={{ minHeight: "200px" }}
              >
                <Table hover borderless className="custom-table align-middle">
                  <thead>
                    <tr>
                      <th>Account Locator</th>
                      <th className="text-right">Credits</th>
                      <th className="text-center">Days</th>
                      <th>Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attributionData.length > 0 ? (
                      attributionData.map((r, i) => (
                        <tr key={i}>
                          <td className="font-weight-bold">
                            {r.CONSUMER_ACCOUNT_LOCATOR || "–"}
                          </td>
                          <td className="text-right text-red font-weight-bold">
                            {parseFloat(r.TOTAL_CREDITS || 0).toFixed(4)}
                          </td>
                          <td className="text-center">
                            <Badge color="soft-blue" pill>
                              {r.USAGE_DAYS || 0}
                            </Badge>
                          </td>
                          <td className="text-muted small">
                            {r.LAST_USAGE
                              ? moment(r.LAST_USAGE).fromNow()
                              : "–"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-4 text-muted">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
      <div className="mt-3 mb-3">
        <Card className="table-card glass-card dropdown-safe-card">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4 correlation-header">
              <h5 className="card-title mb-0">Activity Correlation</h5>
            </div>
            <div
              className="table-responsive-wrapper position-relative  overflow-auto"
              style={{ minHeight: "300px" }}
            >
              {loading && (
                <div className="table-loader-overlay d-flex flex-column align-items-center justify-content-center">
                  <Spinner color="primary" />
                </div>
              )}
              <Table
                hover
                borderless
                className={`custom-table align-middle ${
                  loading ? "table-dimmed" : ""
                }`}
              >
                <thead>
                  <tr>
                    <th>Partner/User</th>
                    <th>Action</th>
                    <th className="text-center">Spikes</th>
                    <th className="text-center">Created At</th>
                    <th className="text-right">Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {correlateData.length > 0 ? (
                    correlateData.map((r, i) => (
                      <tr key={i} className="animate-fadeIn">
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="user-avatar mr-3">
                              {r.activity && r.activity.partner_name
                                ? r.activity.partner_name
                                    .charAt(0)
                                    .toUpperCase()
                                : "U"}
                            </div>
                            <div>
                              <div className="font-weight-bold text-dark small">
                                {r.activity && r.activity.partner_name}
                              </div>
                              <div className="text-xs text-muted">
                                {(r.activity && r.activity.email) || ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge className="badge-soft-blue">
                            {(r.activity && r.activity.activity_type) ||
                              "Activity"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="matched-count">
                            {r.matched_spikes ? r.matched_spikes.length : 0}
                          </div>
                        </td>
                        <td className="text-center text-muted font-weight-medium small">
                          {r.activity && r.activity.createdAt
                            ? moment(r.activity.createdAt).format(
                                "MMM DD, HH:mm"
                              )
                            : "–"}
                        </td>
                        <td className="text-right">
                          <span
                            className={`font-weight-bold ${
                              parseFloat(r.total_credits) > 0
                                ? "text-red"
                                : "text-muted"
                            }`}
                          >
                            {parseFloat(r.total_credits || 0).toFixed(4)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        No correlations found for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {totalPages > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-3 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="text-muted small">
                    {totalRecords > 0 ? (
                      <>
                        Showing{" "}
                        <strong>{(currentPage - 1) * pageSize + 1}</strong> –{" "}
                        <strong>
                          {Math.min(currentPage * pageSize, totalRecords)}
                        </strong>{" "}
                        of <strong>{totalRecords}</strong>
                      </>
                    ) : (
                      "No records found"
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small ml-2">Rows:</span>
                    <Input
                      type="select"
                      size="sm"
                      value={pageSize}
                      className="row-size-selector"
                      onChange={e => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      style={{ width: "85px" }}
                    >
                      {[10, 25, 50, 100].map(size => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Input>
                  </div>
                </div>

                <Pagination size="sm" className="mb-0">
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink first onClick={() => handlePageChange(1)} />
                  </PaginationItem>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                  </PaginationItem>

                  {(() => {
                    const pages = [];
                    const start = Math.max(1, currentPage - 2);
                    const end = Math.min(totalPages, currentPage + 2);

                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <PaginationItem active={i === currentPage} key={i}>
                          <PaginationLink onClick={() => handlePageChange(i)}>
                            {i}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    }

                    return pages;
                  })()}

                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </PaginationItem>
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      last
                      onClick={() => handlePageChange(totalPages)}
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default SnowFlakeAttribution;
